import { useCallback, useEffect, useState } from "react";
import { Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { PURPOSE_LABELS } from "@/lib/cases";
import { AppShell } from "@/components/app/AppShell";
import { Box } from "@/components/app/CaseWork";
import { Input } from "@/components/ui/input";

const run = async (fn, ok, reload) => {
  try {
    await fn();
    toast.success(ok);
    reload();
  } catch (e) {
    toast.error(formatApiError(e));
  }
};

const Approve = ({ o, reload }) => {
  const [purposes, setPurposes] = useState(o.requested_purposes || []);
  const [upload, setUpload] = useState(false);
  const [credits, setCredits] = useState(0);
  const toggle = (p) => setPurposes(purposes.includes(p) ? purposes.filter((x) => x !== p) : [...purposes, p]);
  return (
    <div className="mt-3 grid gap-3" data-testid={`org-approve-${o.id}`}>
      <div className="flex flex-wrap gap-2">{Object.entries(PURPOSE_LABELS).map(([k, l]) => (
        <button key={k} type="button" onClick={() => toggle(k)} data-testid={`org-purpose-${o.id}-${k}`} className={`rounded-full border px-3 py-1 text-xs ${purposes.includes(k) ? "border-oath-ink bg-oath-ink text-white" : "border-gray-200"}`}>{l}</button>
      ))}</div>
      <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={upload} onChange={(e) => setUpload(e.target.checked)} data-testid={`org-upload-${o.id}`} /> May upload its own signed consent forms</label>
      <div className="flex flex-wrap items-center gap-2">
        <Input type="number" min={0} value={credits} onChange={(e) => setCredits(e.target.value)} className="w-28" data-testid={`org-credits-input-${o.id}`} />
        <span className="text-xs text-oath-muted">starting credits</span>
        <button onClick={() => run(() => api.post(`/admin/orgs/${o.id}/approve`, { allowed_purposes: purposes, can_upload_consent: upload, credits: Number(credits) }), "Approved", reload)}
          className="rounded-full bg-emerald-600 px-4 py-2 text-xs font-semibold text-white" data-testid={`org-approve-btn-${o.id}`}>Approve</button>
        <button onClick={() => { const reason = window.prompt("Reason for declining"); if (reason) run(() => api.post(`/admin/orgs/${o.id}/decline`, { reason }), "Declined", reload); }}
          className="rounded-full border border-gray-300 px-4 py-2 text-xs" data-testid={`org-decline-btn-${o.id}`}>Decline</button>
      </div>
    </div>
  );
};

const Credits = ({ o, reload }) => {
  const [n, setN] = useState(10);
  return (
    <div className="mt-2 flex items-center gap-2">
      <Input type="number" value={n} onChange={(e) => setN(e.target.value)} className="h-8 w-24" data-testid={`org-add-credits-input-${o.id}`} />
      <button onClick={() => run(() => api.post(`/admin/orgs/${o.id}/credits`, { add: Number(n) }), "Credits updated", reload)} className="rounded-full border border-gray-300 px-3 py-1 text-xs" data-testid={`org-add-credits-${o.id}`}>Add credits</button>
    </div>
  );
};

const Coverage = () => {
  const [rows, setRows] = useState([]);
  const [f, setF] = useState({ city: "", state: "", zips: "" });
  const load = useCallback(() => api.get("/coverage").then(({ data }) => setRows(data)), []);
  useEffect(() => { load(); }, [load]);
  return (
    <Box title="FIELD VISIT COVERAGE" testid="coverage-panel">
      <ul className="grid gap-2">{rows.map((c) => (
        <li key={c.id} className="flex items-center justify-between rounded-xl bg-oath-bg/60 px-3 py-2 text-sm" data-testid={`coverage-${c.city.replace(/\s/g, "-").toLowerCase()}`}>
          <span><b>{c.city}, {c.state}</b> <span className="text-xs text-oath-muted">ZIP {c.zip_prefixes.join(", ")}xx</span></span>
          <button onClick={() => run(() => api.delete(`/admin/coverage/${c.id}`), "Removed", load)} aria-label="Remove" className="text-oath-muted hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
        </li>
      ))}</ul>
      <div className="mt-3 grid gap-2 sm:grid-cols-[1fr_70px_1fr_auto]">
        <Input placeholder="City" value={f.city} onChange={(e) => setF({ ...f, city: e.target.value })} data-testid="coverage-city" />
        <Input placeholder="ST" maxLength={2} value={f.state} onChange={(e) => setF({ ...f, state: e.target.value })} data-testid="coverage-state" />
        <Input placeholder="ZIP prefixes, e.g. 331, 332" value={f.zips} onChange={(e) => setF({ ...f, zips: e.target.value })} data-testid="coverage-zips" />
        <button onClick={() => run(() => api.post("/admin/coverage", { city: f.city, state: f.state, zip_prefixes: f.zips.split(",") }), "Added", () => { setF({ city: "", state: "", zips: "" }); load(); })} className="rounded-full bg-oath-ink px-4 py-2 text-xs font-semibold text-white" data-testid="coverage-add">Add</button>
      </div>
    </Box>
  );
};

const Agents = () => {
  const [rows, setRows] = useState([]);
  const load = useCallback(() => api.get("/admin/agents").then(({ data }) => setRows(data)), []);
  useEffect(() => { load(); }, [load]);
  const save = (a, k, v) => run(() => api.put(`/admin/agents/${a.id}`, { ...(a.contractor || {}), [k]: v }), "Saved", load);
  return (
    <Box title="FIELD AGENTS (INDEPENDENT CONTRACTORS)" testid="agents-panel">
      {rows.length === 0 && <p className="text-sm text-oath-muted">Invite field agents from Users & invites with the Field Agent role.</p>}
      {rows.map((a) => (
        <div key={a.id} className="mb-3 grid gap-2 rounded-xl bg-oath-bg/60 p-3 sm:grid-cols-4" data-testid={`agent-${a.id}`}>
          <div className="text-sm"><b>{a.name || a.email}</b><div className="text-xs text-oath-muted">{a.email}</div></div>
          <Input defaultValue={a.contractor?.contractor_company} placeholder="Contractor company" data-testid={`agent-company-${a.id}`} onBlur={(e) => save(a, "contractor_company", e.target.value)} />
          <Input defaultValue={a.contractor?.license_number} placeholder="License #" data-testid={`agent-license-${a.id}`} onBlur={(e) => save(a, "license_number", e.target.value)} />
          <Input type="date" defaultValue={a.contractor?.agreement_signed_on} title="Contractor agreement signed" data-testid={`agent-agreement-${a.id}`} onBlur={(e) => save(a, "agreement_signed_on", e.target.value)} />
        </div>
      ))}
    </Box>
  );
};

export default function AdminOrgs() {
  const [orgs, setOrgs] = useState(null);
  const load = useCallback(() => api.get("/admin/orgs").then(({ data }) => setOrgs(data)).catch((e) => toast.error(formatApiError(e))), []);
  useEffect(() => { load(); }, [load]);
  return (
    <AppShell eyebrow="— SUPER ADMIN" title="Organizations & field coverage">
      <div className="grid gap-4">
        {orgs === null && <Loader2 className="h-5 w-5 animate-spin" />}
        {orgs?.map((o) => (
          <div key={o.id} className="rounded-2xl border border-gray-200 bg-white p-5" data-testid={`org-row-${o.id}`}>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div><b className="text-oath-ink">{o.name}</b> <span className="text-xs text-oath-muted">{o.industry} · License {o.license_number || "—"} · {o.user_count} user(s)</span></div>
              <span className={`rounded-full px-2.5 py-0.5 font-mono-label text-[9px] ${o.status === "pending" ? "bg-amber-50 text-amber-700" : o.status === "active" ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`} data-testid={`org-status-${o.id}`}>{o.status.toUpperCase()}</span>
            </div>
            {o.status === "pending" ? <Approve o={o} reload={load} /> : (
              <div className="mt-2 text-sm text-oath-secondary">
                Credits: <b data-testid={`org-credits-${o.id}`}>{o.credits ?? 0}</b> · Purposes: {(o.allowed_purposes || []).map((p) => PURPOSE_LABELS[p]).join(", ") || "all"} · Own consent forms: {o.can_upload_consent ? "yes" : "no"}
                {o.status === "active" && <Credits o={o} reload={load} />}
              </div>
            )}
          </div>
        ))}
        <Coverage />
        <Agents />
      </div>
    </AppShell>
  );
}
